import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useDialers } from '../api';
import type { DialerRow } from '../types';

const POLL_MS = 45_000;

export function DialerListPage() {
  const navigate = useNavigate();
  const query = useDialers({ refetchIntervalMs: POLL_MS });
  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<DialerRow>[]>(
    () => [
      {
        accessorKey: 'dialerId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.dialerId}
          </span>
        ),
      },
      {
        accessorKey: 'dialerName',
        header: 'Dialer name',
        cell: ({ row }) => (
          <Link
            to={`/update-dialer/${row.original.dialerId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.dialerName}
          </Link>
        ),
      },
      { accessorKey: 'dialerIP', header: 'IP', size: 130 },
      { accessorKey: 'dialerPort', header: 'Port', size: 80 },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 130 },
      { accessorKey: 'activeCli', header: 'Active CLI', size: 110 },
      { accessorKey: 'dialerCalls', header: 'Active calls', size: 110 },
      { accessorKey: 'totalChannels', header: 'Total channels', size: 130 },
      { accessorKey: 'channelReduction', header: 'Channel reduction', size: 150 },
      { accessorKey: 'cpsReduction', header: 'CPS reduction', size: 130 },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.dialerName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/update-dialer/${row.original.dialerId}`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate],
  );

  return (
    <FillPage>
      <PageHeader
        title="All Dialers"
        description="Live view of the dialer fleet. Refreshes automatically every 45 seconds."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw
                className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              />
            </Button>
            <Button asChild variant="outline">
              <Link to="/search-dialer">
                <Search className="h-4 w-4" />
                Search by dialer
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/dialer-usage">Usage</Link>
            </Button>
            <Button asChild>
              <Link to="/create-dialer">
                <Plus className="h-4 w-4" />
                Add dialer
              </Link>
            </Button>
          </div>
        }
      />
      <DataTable<DialerRow, unknown>
        tableId="dialers:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search dialers…"
        emptyTitle="No dialers found"
        emptyDescription="Add a dialer to start routing calls."
        getRowId={(row) => String(row.dialerId)}
        exportFilename="dialers.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />
    </FillPage>
  );
}
